import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Accessibility,
  X,
  Search,
  Type,
  Contrast,
  AlignJustify,
  Underline,
  AlignHorizontalJustifyCenter,
  Zap,
  Volume2,
  VolumeX,
  RotateCcw,
} from 'lucide-react';
import { useAccessibility } from '../../contexts/AccessibilityContext';
import { MagnifierLens } from './MagnifierLens';
import { ReadingGuide } from './ReadingGuide';

const Toggle = ({ active, onClick, icon: Icon, label, description, testId }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    data-testid={testId}
    className={`w-full flex items-start gap-3 p-3 rounded-lg border-2 text-left transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 ${
      active
        ? 'bg-teal-50 border-teal-500 text-teal-900'
        : 'bg-white border-gray-200 hover:border-teal-300 text-gray-800'
    }`}
  >
    <span
      className={`shrink-0 w-9 h-9 rounded-md flex items-center justify-center ${
        active ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-700'
      }`}
      aria-hidden="true"
    >
      <Icon size={18} />
    </span>
    <span className="flex-1 min-w-0">
      <span className="block text-sm font-semibold">{label}</span>
      {description && (
        <span className="block text-xs text-gray-600 mt-0.5">{description}</span>
      )}
    </span>
    <span
      className={`shrink-0 mt-1 inline-flex items-center justify-center text-[10px] font-bold px-2 py-0.5 rounded-full ${
        active ? 'bg-teal-600 text-white' : 'bg-gray-200 text-gray-600'
      }`}
      aria-hidden="true"
    >
      {active ? 'ON' : 'OFF'}
    </span>
  </button>
);

const FontSizeControl = ({ value, onChange, t }) => (
  <div className="bg-white border-2 border-gray-200 rounded-lg p-3">
    <div className="flex items-start gap-3">
      <span className="shrink-0 w-9 h-9 rounded-md bg-gray-100 text-gray-700 flex items-center justify-center" aria-hidden="true">
        <Type size={18} />
      </span>
      <div className="flex-1">
        <div className="text-sm font-semibold text-gray-800">
          {t('a11y.fontSize.label')}
        </div>
        <div className="text-xs text-gray-600 mb-2">
          {t('a11y.fontSize.desc')}
        </div>
        <div className="grid grid-cols-3 gap-2" role="group" aria-label={t('a11y.fontSize.label')}>
          {[
            { v: 0, label: 'A', size: 'text-base' },
            { v: 1, label: 'A+', size: 'text-lg' },
            { v: 2, label: 'A++', size: 'text-xl' },
          ].map((opt) => (
            <button
              key={opt.v}
              type="button"
              onClick={() => onChange(opt.v)}
              aria-pressed={value === opt.v}
              data-testid={`a11y-font-size-${opt.v}`}
              className={`py-2 rounded-md font-bold border-2 transition-colors ${opt.size} ${
                value === opt.v
                  ? 'bg-teal-600 border-teal-600 text-white'
                  : 'bg-white border-gray-200 text-gray-700 hover:border-teal-300'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  </div>
);

export const AccessibilityWidget = () => {
  const { t } = useTranslation();
  const { prefs, update, toggle, reset, speak, stopSpeaking } = useAccessibility();
  const [open, setOpen] = useState(false);
  const panelRef = useRef(null);
  const triggerRef = useRef(null);

  // Close on Escape, focus trap-lite
  useEffect(() => {
    if (!open) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handleKey);
    // Focus first focusable inside the panel
    const first = panelRef.current?.querySelector('button, [href], input, select');
    first?.focus();
    return () => document.removeEventListener('keydown', handleKey);
  }, [open]);

  // Read-aloud: when enabled, click on any text reads it
  useEffect(() => {
    if (!prefs.ttsEnabled) {
      stopSpeaking();
      return;
    }
    const handleClick = (e) => {
      const target = e.target.closest('[data-a11y-widget]');
      if (target) return; // ignore clicks inside our own UI
      const el = e.target.closest('p, h1, h2, h3, h4, h5, h6, li, a, button, span, label');
      if (!el) return;
      const text = (el.innerText || '').trim();
      if (text && text.length < 600) speak(text);
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [prefs.ttsEnabled, speak, stopSpeaking]);

  // Listen for external trigger (e.g., AccessibilityBadge)
  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener('chico:open-a11y', handler);
    return () => window.removeEventListener('chico:open-a11y', handler);
  }, []);

  return (
    <>
      <MagnifierLens enabled={prefs.magnifier} />
      <ReadingGuide enabled={prefs.readingGuide} />

      {/* Floating Trigger Button */}
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls="a11y-panel"
        aria-label={t('a11y.openPanel')}
        data-a11y-widget="trigger"
        data-testid="a11y-trigger"
        className="fixed bottom-6 right-6 z-[2147483400] w-14 h-14 rounded-full bg-teal-600 hover:bg-teal-700 text-white shadow-2xl flex items-center justify-center focus:outline-none focus:ring-4 focus:ring-teal-300 transition-transform hover:scale-105"
      >
        <Accessibility size={26} aria-hidden="true" />
      </button>

      {/* Panel Overlay */}
      {open && (
        <div
          data-a11y-widget="overlay"
          className="fixed inset-0 z-[2147483450] bg-black/40 backdrop-blur-[2px]"
          onClick={() => setOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Panel */}
      <aside
        id="a11y-panel"
        ref={panelRef}
        data-a11y-widget="panel"
        data-testid="a11y-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="a11y-panel-title"
        className={`fixed top-0 right-0 h-full w-full sm:w-[380px] bg-gray-50 z-[2147483460] shadow-2xl transition-transform duration-300 flex flex-col ${
          open ? 'translate-x-0' : 'translate-x-full pointer-events-none'
        }`}
      >
        <header className="flex items-center justify-between px-5 py-4 bg-white border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Accessibility size={22} className="text-teal-600" aria-hidden="true" />
            <h2 id="a11y-panel-title" className="text-base font-bold text-gray-900">
              {t('a11y.title')}
            </h2>
          </div>
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label={t('a11y.close')}
            data-testid="a11y-close"
            className="w-9 h-9 rounded-md text-gray-600 hover:bg-gray-100 flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-teal-500"
          >
            <X size={20} aria-hidden="true" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          <p className="text-xs text-gray-600 leading-relaxed">
            {t('a11y.subtitle')}
          </p>

          <Toggle
            active={prefs.magnifier}
            onClick={() => toggle('magnifier')}
            icon={Search}
            label={t('a11y.magnifier.label')}
            description={t('a11y.magnifier.desc')}
            testId="a11y-toggle-magnifier"
          />

          <FontSizeControl
            value={prefs.fontSize}
            onChange={(v) => update({ fontSize: v })}
            t={t}
          />

          <Toggle
            active={prefs.highContrast}
            onClick={() => toggle('highContrast')}
            icon={Contrast}
            label={t('a11y.highContrast.label')}
            description={t('a11y.highContrast.desc')}
            testId="a11y-toggle-contrast"
          />

          <Toggle
            active={prefs.dyslexiaFont}
            onClick={() => toggle('dyslexiaFont')}
            icon={AlignJustify}
            label={t('a11y.dyslexia.label')}
            description={t('a11y.dyslexia.desc')}
            testId="a11y-toggle-dyslexia"
          />

          <Toggle
            active={prefs.underlineLinks}
            onClick={() => toggle('underlineLinks')}
            icon={Underline}
            label={t('a11y.underline.label')}
            description={t('a11y.underline.desc')}
            testId="a11y-toggle-underline"
          />

          <Toggle
            active={prefs.readingGuide}
            onClick={() => toggle('readingGuide')}
            icon={AlignHorizontalJustifyCenter}
            label={t('a11y.readingGuide.label')}
            description={t('a11y.readingGuide.desc')}
            testId="a11y-toggle-guide"
          />

          <Toggle
            active={prefs.reduceMotion}
            onClick={() => toggle('reduceMotion')}
            icon={Zap}
            label={t('a11y.reduceMotion.label')}
            description={t('a11y.reduceMotion.desc')}
            testId="a11y-toggle-motion"
          />

          <Toggle
            active={prefs.ttsEnabled}
            onClick={() => toggle('ttsEnabled')}
            icon={prefs.ttsEnabled ? Volume2 : VolumeX}
            label={t('a11y.tts.label')}
            description={t('a11y.tts.desc')}
            testId="a11y-toggle-tts"
          />
        </div>

        <footer className="px-5 py-4 bg-white border-t border-gray-200">
          <button
            type="button"
            onClick={reset}
            data-testid="a11y-reset"
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-800 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-teal-500"
          >
            <RotateCcw size={16} aria-hidden="true" />
            {t('a11y.reset')}
          </button>
          <p className="text-[11px] text-center text-gray-500 mt-2">
            {t('a11y.footer')}
          </p>
        </footer>
      </aside>
    </>
  );
};

export default AccessibilityWidget;
